import type { HTMLProps } from "react";
import { useState } from "react";

import { InputIcon, TextInput } from "@ui/components/Inputs";
import { RoundButton } from "@ui/components/RoundButton";
import { useTranslations } from "@ui/providers/useTranslations";
import { formatDate } from "@ui/utils/formatDate/formatDate";
import classNames from "classnames";

import { EditIcon } from "@jti/brand-icons/edit";
import { UiCrossIcon } from "@jti/brand-icons/ui-cross";

import { DatePicker } from "../DatePicker";

import styles from "./DatePickerInput.module.scss";

type DatePickerInputProps = {
  currentlyBookedDate?: Date;
  onDateSelect?: (date: Date | null) => void;
} & HTMLProps<HTMLDivElement>;

export const DatePickerInput = ({ currentlyBookedDate, onDateSelect, ...restProps }: DatePickerInputProps) => {
  const { __ } = useTranslations();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const initialValue = formatDate(currentlyBookedDate) || __("profile.subscription.editBilling.select_day");
  const [inputValue, setInputValue] = useState(initialValue);

  const handleInputClick = () => {
    setIsOpen((prevIsOpen) => !prevIsOpen);
  };

  const handleSaveSelection = (selectedDate: Date | null) => {
    if (!selectedDate) {
      return;
    }

    setInputValue(formatDate(selectedDate));
    setIsOpen(false);

    onDateSelect?.(selectedDate);
  };
  return (
    <div {...restProps}>
      {isOpen ? (
        <div className={classNames("light-theme", styles.calendarBox)} data-testid="date-picker">
          <div className={styles.calendarHeader}>
            <RoundButton icon={UiCrossIcon} onClick={() => setIsOpen(false)} aria-label={__("common.close")} />
          </div>
          <DatePicker onSaveSelection={handleSaveSelection} {...(currentlyBookedDate ? { currentlyBookedDate } : {})} />
        </div>
      ) : (
        <div
          className={styles.inputBox}
          onClick={handleInputClick}
          role="button"
          aria-label={__("profile.subscription.editBilling.title")}
        >
          <TextInput
            id="date-picker-input"
            placeholder={inputValue}
            value={""}
            type="text"
            data-testid={"datePickerInput"}
            elemAfter={<InputIcon icon={EditIcon} />}
          />
        </div>
      )}
    </div>
  );
};
